import { useEffect, useState } from "react";
import { SealCheckIcon as SealCheck } from "@phosphor-icons/react";
import ValidationBadge from "@/components/validation/ValidationBadge";
import DecisionButtons from "@/components/validation/DecisionButtons";
import { formatDecided } from "@/lib/validationConstants";

/** Detail-view aside: consultant review of an AI deliverable (Iter 40). */
export default function DeliverableValidationPanel({ deliverable, canEdit, busy, onDecide }) {
  const [note, setNote] = useState(deliverable.validation_note || "");
  useEffect(() => { setNote(deliverable.validation_note || ""); }, [deliverable.id, deliverable.validation_note]);

  const decided = formatDecided(deliverable);
  const notReady = deliverable.status !== "ready";
  return (
    <div className="ml-card p-5 mb-4" data-testid="deliverable-validation-panel">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="ml-eyebrow flex items-center gap-1.5">
          <SealCheck size={12} weight="bold" /> Consultant review
        </div>
        <ValidationBadge state={deliverable.validation_state} row={deliverable} testid="deliverable-validation-badge" />
      </div>
      <p className="text-xs text-[var(--ml-text-secondary)] leading-relaxed mb-3" data-testid="deliverable-validation-hint">
        {deliverable.validation_state === "validated"
          ? <>Validated by <strong>{decided}</strong>. Exports carry this attribution.</>
          : deliverable.validation_state === "rejected"
            ? <>Rejected by <strong>{decided}</strong>. Not suitable for client use as written.</>
            : <>This document was generated by the AI and has <strong>not</strong> been validated by a consultant. Review it before it reaches a client.</>}
      </p>
      <textarea
        className="ml-textarea min-h-[64px] text-sm"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        disabled={!canEdit || busy}
        placeholder="Review note (what was checked, caveats, who signed off)"
        data-testid="deliverable-validation-note"
      />
      {deliverable.validation_note && deliverable.validation_note !== note && (
        <div className="text-[11px] text-[var(--ml-text-secondary)] mt-1">Saved note: {deliverable.validation_note}</div>
      )}
      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
        {canEdit ? (
          <DecisionButtons
            state={deliverable.validation_state}
            kind="deliverable"
            busy={busy}
            disabled={notReady && deliverable.validation_state !== "validated" && deliverable.validation_state !== "rejected"}
            onDecide={(d) => onDecide(d, note.trim() || null)}
            testidPrefix="deliverable-decision"
          />
        ) : (
          <span className="text-xs text-[var(--ml-text-secondary)]" data-testid="deliverable-validation-readonly">
            Owner or editor role required to validate.
          </span>
        )}
        {notReady && (
          <span className="text-[11px] text-[var(--ml-text-secondary)]" data-testid="deliverable-validation-notready">
            Only a finished deliverable can be validated.
          </span>
        )}
      </div>
    </div>
  );
}
